import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Printer,
  Edit,
  Download,
  Loader2,
  RectangleHorizontal,
  RectangleVertical,
  Minus,
  Leaf,
  Droplet,
  Coffee,
  Cookie,
  Utensils,
  Heart,
  Sparkle,
  Grid3X3,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import QRCode from 'qrcode';
import type { Product } from '../types';

interface QuickPrintModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onEditInLabelForge?: (product: Product) => void;
}

type LabelCategory = 'CANNABIS' | 'HASHISH' | 'COFFEE' | 'CACAO' | 'GASTRO' | 'COSMETICS' | 'CUSTOM';
type LabelLayout = 'LANDSCAPE' | 'PORTRAIT' | 'SHELF';

interface LabelPreview {
  productId: string;
  name: string;
  category: LabelCategory;
  sku?: string;
  price?: string;
  weight?: string;
  description?: string;
  brand?: string;
  imageUrl?: string;
  layout: string;
  qrEnabled: boolean;
  qrContent?: string;
  primaryColor: string;
  secondaryColor: string;
}

const CATEGORIES = {
  CANNABIS: { name: 'Cannabis', color: '#22c55e', icon: Leaf },
  HASHISH: { name: 'Extract', color: '#f59e0b', icon: Droplet },
  COFFEE: { name: 'Coffee', color: '#78350f', icon: Coffee },
  CACAO: { name: 'Cacao', color: '#92400e', icon: Cookie },
  GASTRO: { name: 'Gastro', color: '#3b82f6', icon: Utensils },
  COSMETICS: { name: 'Cosmetics', color: '#ec4899', icon: Heart },
  CUSTOM: { name: 'Custom', color: '#6366f1', icon: Sparkle },
};

const LAYOUTS = {
  LANDSCAPE: { name: 'Landscape', width: 420, height: 280, icon: RectangleHorizontal },
  PORTRAIT: { name: 'Portrait', width: 280, height: 400, icon: RectangleVertical },
  SHELF: { name: 'Shelf', width: 350, height: 150, icon: Minus },
};

export function QuickPrintModal({
  product,
  isOpen,
  onClose,
  onEditInLabelForge,
}: QuickPrintModalProps) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<LabelLayout>('LANDSCAPE');
  const [category, setCategory] = useState<LabelCategory>('CUSTOM');
  const [qrEnabled, setQrEnabled] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [slogan, setSlogan] = useState('');

  // Fetch label preview from product
  const { data: previewData, isLoading: isLoadingPreview } = useQuery({
    queryKey: ['label-preview', product?.id, layout, category],
    queryFn: () =>
      api.get<{ labelPreview: LabelPreview; product: Product }>(
        `/labels/preview-from-product/${product?.id}?layout=${layout}&category=${category}`
      ),
    enabled: !!product?.id && isOpen,
  });

  const labelPreview = previewData?.labelPreview;

  // Create label mutation
  const createLabelMutation = useMutation({
    mutationFn: (data: {
      layout: LabelLayout;
      category?: LabelCategory;
      qrEnabled: boolean;
      slogan?: string;
    }) =>
      api.post<{ label: any; product: Product }>(
        `/labels/from-product/${product?.id}`,
        data
      ),
    onSuccess: () => {
      toast.success('Label saved to library!');
    },
    onError: () => {
      toast.error('Failed to create label');
    },
  });

  // Generate QR code
  useEffect(() => {
    if (qrEnabled && labelPreview?.qrContent) {
      QRCode.toDataURL(labelPreview.qrContent, {
        width: 100,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(''));
    } else {
      setQrDataUrl('');
    }
  }, [qrEnabled, labelPreview?.qrContent]);

  // Auto-detect category from product
  useEffect(() => {
    if (product?.categoryName) {
      const lower = product.categoryName.toLowerCase();
      if (lower.includes('cannabis') || lower.includes('flower')) setCategory('CANNABIS');
      else if (lower.includes('hash') || lower.includes('extract')) setCategory('HASHISH');
      else if (lower.includes('coffee')) setCategory('COFFEE');
      else if (lower.includes('cacao') || lower.includes('chocolate')) setCategory('CACAO');
      else if (lower.includes('cosmetic')) setCategory('COSMETICS');
      else if (lower.includes('food') || lower.includes('gastro')) setCategory('GASTRO');
      else setCategory('CUSTOM');
    }
  }, [product?.categoryName]);

  const handleExport = async (format: 'png' | 'pdf') => {
    if (!previewRef.current) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
      });

      const layoutConfig = LAYOUTS[layout];

      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `${product?.name || 'label'}_${category}_${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
        toast.success('PNG exported!');
      } else {
        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({
          orientation: layoutConfig.width > layoutConfig.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [layoutConfig.width * 2, layoutConfig.height * 2],
        });
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0,
          0,
          layoutConfig.width * 2,
          layoutConfig.height * 2
        );
        pdf.save(`${product?.name || 'label'}_${category}_${Date.now()}.pdf`);
        toast.success('PDF exported!');
      }
    } catch (error) {
      console.error(error);
      toast.error('Export failed');
    }
  };

  const handleSaveAndPrint = async () => {
    await createLabelMutation.mutateAsync({
      layout,
      category,
      qrEnabled,
      slogan: slogan || undefined,
    });
    await handleExport('png');
  };

  const handleEditInLabelForge = () => {
    if (product && onEditInLabelForge) {
      onEditInLabelForge(product);
      onClose();
    }
  };

  if (!isOpen) return null;

  const categoryConfig = CATEGORIES[category];
  const layoutConfig = LAYOUTS[layout];
  const primaryColor = categoryConfig.color;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Printer size={24} className="text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Quick Print Label</h2>
                <p className="text-sm text-slate-500">
                  {product?.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-green-500" />
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Settings Panel */}
                <div className="space-y-4">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Category
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {(Object.entries(CATEGORIES) as [LabelCategory, typeof CATEGORIES.CANNABIS][]).map(
                        ([key, cat]) => (
                          <button
                            key={key}
                            onClick={() => setCategory(key)}
                            className={clsx(
                              'p-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-1',
                              category === key
                                ? 'ring-2 ring-white/50 scale-105 text-white'
                                : 'hover:scale-102 opacity-70 hover:opacity-100 text-white'
                            )}
                            style={{
                              background: `linear-gradient(135deg, ${cat.color}, ${cat.color}aa)`,
                            }}
                          >
                            <cat.icon size={20} />
                            <span className="truncate w-full text-center">{cat.name}</span>
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  {/* Layout Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Layout
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(Object.entries(LAYOUTS) as [LabelLayout, typeof LAYOUTS.LANDSCAPE][]).map(
                        ([key, lay]) => (
                          <button
                            key={key}
                            onClick={() => setLayout(key)}
                            className={clsx(
                              'p-3 rounded-lg text-sm font-medium transition-all flex flex-col items-center gap-1',
                              layout === key
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-slate-700 hover:bg-gray-200 border border-gray-300'
                            )}
                          >
                            <lay.icon size={24} />
                            <span>{lay.name}</span>
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  {/* Slogan */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Slogan / Tagline (optional)
                    </label>
                    <input
                      type="text"
                      value={slogan}
                      onChange={(e) => setSlogan(e.target.value)}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-slate-900 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                      placeholder="Premium Quality"
                    />
                  </div>

                  {/* QR Code Toggle */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2">
                      <Grid3X3 size={20} className="text-slate-500" />
                      <span className="text-sm text-slate-700">QR Code</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={qrEnabled}
                        onChange={(e) => setQrEnabled(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>

                  {/* Product Info */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-medium text-slate-900 mb-2">Product Data</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-slate-500">Name:</div>
                      <div className="text-slate-900">{product?.name}</div>
                      <div className="text-slate-500">SKU:</div>
                      <div className="text-slate-900 font-mono">{product?.code || '-'}</div>
                      <div className="text-slate-500">Price:</div>
                      <div className="text-slate-900">
                        {product?.currency === 'EUR' ? '€' : product?.currency}
                        {product?.price}
                      </div>
                      {product?.store && (
                        <>
                          <div className="text-slate-500">Store:</div>
                          <div className="text-slate-900">{product.store.name}</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Preview Panel */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-slate-700">Live Preview</h3>
                    <span className="text-xs text-slate-500">
                      {layoutConfig.width}×{layoutConfig.height}px
                    </span>
                  </div>

                  <div className="flex items-center justify-center bg-gray-100 rounded-xl p-6 min-h-[350px] border border-gray-200">
                    <div
                      ref={previewRef}
                      className="relative rounded-xl shadow-2xl overflow-hidden transition-all duration-300"
                      style={{
                        width: layoutConfig.width * 0.85,
                        height: layoutConfig.height * 0.85,
                        background: `linear-gradient(135deg, ${primaryColor}15, #1f293730)`,
                        border: `3px solid ${primaryColor}`,
                      }}
                    >
                      {/* Background Image */}
                      {labelPreview?.imageUrl && (
                        <div
                          className="absolute inset-0 opacity-20"
                          style={{
                            backgroundImage: `url(${labelPreview.imageUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        />
                      )}

                      {/* Top Color Bar */}
                      <div
                        className="h-2 w-full"
                        style={{ backgroundColor: primaryColor }}
                      />

                      <div className="relative p-4 h-full flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {(() => {
                              const CategoryIcon = categoryConfig.icon;
                              return <CategoryIcon size={20} style={{ color: primaryColor }} />;
                            })()}
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold uppercase text-white"
                              style={{ backgroundColor: primaryColor }}
                            >
                              {categoryConfig.name}
                            </span>
                          </div>
                          {labelPreview?.sku && (
                            <span className="text-xs text-slate-500 font-mono">
                              #{labelPreview.sku}
                            </span>
                          )}
                        </div>

                        {/* Brand */}
                        {labelPreview?.brand && (
                          <p
                            className="text-xs font-bold tracking-widest mb-1"
                            style={{ color: primaryColor }}
                          >
                            {labelPreview.brand}
                          </p>
                        )}

                        {/* Product Name */}
                        <h2 className="text-xl font-bold text-slate-900 mb-1 leading-tight">
                          {labelPreview?.name || product?.name}
                        </h2>

                        {/* Slogan */}
                        {slogan && (
                          <p className="text-sm text-slate-600 italic mb-2">{slogan}</p>
                        )}

                        {/* Spacer */}
                        <div className="flex-1" />

                        {/* Bottom Row */}
                        <div className="flex items-end justify-between mt-2 pt-2 border-t border-slate-200">
                          <div>
                            {labelPreview?.price && (
                              <p
                                className="text-2xl font-bold"
                                style={{ color: primaryColor }}
                              >
                                {labelPreview.price}
                              </p>
                            )}
                            {labelPreview?.weight && (
                              <p className="text-sm text-slate-500">{labelPreview.weight}</p>
                            )}
                          </div>

                          {/* Product Image */}
                          {labelPreview?.imageUrl && (
                            <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-white shadow-lg">
                              <img
                                src={labelPreview.imageUrl}
                                alt="Product"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}

                          {/* QR Code */}
                          {qrEnabled && qrDataUrl && (
                            <div className="bg-white p-1 rounded-lg shadow-md">
                              <img src={qrDataUrl} alt="QR" className="w-12 h-12" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleEditInLabelForge}
              className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:text-slate-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Edit size={18} />
              Edit in LabelForge
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={() => handleExport('png')}
                disabled={isLoadingPreview}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Download size={18} />
                Export PNG
              </button>
              <button
                onClick={() => handleExport('pdf')}
                disabled={isLoadingPreview}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Download size={18} />
                Export PDF
              </button>
              <button
                onClick={handleSaveAndPrint}
                disabled={isLoadingPreview || createLabelMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {createLabelMutation.isPending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Printer size={18} />
                )}
                Save & Print
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
